import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const SyncAnalytics = ({ tenantId }) => {
  const [analytics, setAnalytics] = useState({
    syncFrequency: 0,
    errorRate: 0,
    avgResponseTime: 0,
    peakSyncHours: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      loadAnalytics();
    }
  }, [tenantId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('pos_inventory_logs')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString());

      if (error) throw error;

      const totalSyncs = data?.length || 0;
      const errorSyncs = data?.filter(log => log?.reason === 'error')?.length || 0;
      
      setAnalytics({
        syncFrequency: Math.round(totalSyncs / 7),
        errorRate: totalSyncs > 0 ? ((errorSyncs / totalSyncs) * 100)?.toFixed(2) : 0,
        avgResponseTime: 1.2,
        peakSyncHours: ['9:00 AM', '12:00 PM', '3:00 PM', '6:00 PM']
      });
    } catch (error) {
      console.error('Load analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const recommendations = [
    {
      id: 1,
      title: 'Optimize Sync Frequency',
      description: 'Consider increasing sync intervals during off-peak hours to reduce server load',
      priority: 'medium',
      impact: 'Performance'
    },
    {
      id: 2,
      title: 'Enable Batch Processing',
      description: 'Group multiple inventory updates into batch operations for better efficiency',
      priority: 'high',
      impact: 'Performance'
    },
    {
      id: 3,
      title: 'Implement Retry Logic',
      description: 'Add automatic retry mechanism for failed sync operations',
      priority: 'high',
      impact: 'Reliability'
    },
    {
      id: 4,
      title: 'Monitor Peak Hours',
      description: 'Allocate additional resources during peak sync hours (9 AM - 6 PM)',
      priority: 'low',
      impact: 'Scalability'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-danger';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-danger/10';
      case 'medium':
        return 'bg-warning/10';
      case 'low':
        return 'bg-primary/10';
      default:
        return 'bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Sync Frequency</span>
            <Icon name="Activity" size={20} className="text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{analytics?.syncFrequency}</p>
          <p className="text-xs text-muted-foreground mt-1">Syncs per day (avg)</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Error Rate</span>
            <Icon name="AlertCircle" size={20} className="text-danger" />
          </div>
          <p className="text-2xl font-bold text-danger">{analytics?.errorRate}%</p>
          <p className="text-xs text-muted-foreground mt-1">Failed operations</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Avg Response Time</span>
            <Icon name="Zap" size={20} className="text-success" />
          </div>
          <p className="text-2xl font-bold text-success">{analytics?.avgResponseTime}s</p>
          <p className="text-xs text-muted-foreground mt-1">Sync duration</p>
        </div>

        <div className="bg-card rounded-lg p-6 border border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">Peak Hours</span>
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <p className="text-2xl font-bold text-foreground">4</p>
          <p className="text-xs text-muted-foreground mt-1">High activity periods</p>
        </div>
      </div>

      {/* Performance Recommendations */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Performance Recommendations</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Automated suggestions to optimize sync operations
          </p>
        </div>
        <div className="divide-y divide-border">
          {recommendations?.map((rec) => (
            <div key={rec?.id} className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Lightbulb" size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{rec?.title}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      getPriorityBg(rec?.priority)
                    } ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{rec?.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Impact:</span>
                    <span className="text-xs font-medium text-primary">{rec?.impact}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Peak Sync Hours */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Peak Sync Hours</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {analytics?.peakSyncHours?.map((hour, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4 text-center">
              <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
              <p className="font-semibold text-foreground">{hour}</p>
              <p className="text-xs text-muted-foreground mt-1">High activity</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SyncAnalytics;